/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import * as X from "@/ui";
import { useFormContext, Controller, type FieldValues, type Path } from "react-hook-form";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { fieldError, type FieldError } from "@/types";
import Select, { components } from "react-select";

import WarningImage from "@/assets/shared-form-input-warning.svg";
import CheckboxChecked from "@/assets/shared-form-checkbox-checked.svg";
import CheckboxUnchecked from "@/assets/shared-form-checkbox-unchecked.svg";

/**
 * preload static fallback, in case of the fetching would fail.
 *
 * this can probably be removed to get a slightly lighter page, or keep to
 * improve reliability.
 */
import DefaultCountryOptions from "./countries.json";
import { JSX } from "react";
import ReactMarkdown from "react-markdown";


export { fieldError, type FieldError };


type CountryOptionsCache = (
  | { type: "idle" }
  | { type: "loading", optionsPromise: Promise<SelectOption[]> }
  | { type: "ready", options: SelectOption[] }
);

const countryOptionsCache: { [k in X.Locale]: CountryOptionsCache } = {
  en: { type: "idle" },
  fr: { type: "idle" },
  zh: { type: "idle" },
  ko: { type: "idle" }
};

/**
 * use this in pages that will eventually open a modal containing a form with a phone field.
 */
export function usePreloadPhoneCountryOptions() {
  X.useMountEffect(() => {
    getAllCountryOptions();
  });
}

export type TextareaInputProps = React.ComponentProps<typeof TextareaInput>;

export function TextareaInput<TFormValues extends FieldValues>(props: {
  name: Path<TFormValues>;
  label: string;
  invalidMessage?: string;
  DynamicErrorView?: JSX.Element
}) {
  const { register, formState: { errors } } = useFormContext<TFormValues>();
  const error = errors[props.name]?.message?.toString();
  // console.log({ error })
  return (
    <div>
      <div className="relative">
        <textarea
          {...register(props.name as any)}
          placeholder={props.label}
          rows={3}
          className={X.clsx(
            "w-full border rounded-lg px-4",
            error ? "border-danger" : "border-black",
            "placeholder:text-[12px] placeholder:text-gray ",
            "text-[14px] p-4",
            "focus:border-black focus:outline-none",
          )}
        />
        {error && (
          <X.Image
            src={WarningImage}
            alt="Warning"
            className="absolute w-[20px] h-auto top-1/2 right-4 -translate-y-1/2"
          />
        )}
      </div>

      {props.DynamicErrorView ? props.DynamicErrorView :
        <ErrorView
          error={error}
          invalidMessage={props.invalidMessage}
          requiredMessage={
            "Veuillez sélectionner une valeur"
          }
        />}
    </div>
  );
}


export type InputFieldProps = React.ComponentProps<typeof InputField>;

export function InputField<TFormValues extends FieldValues>(props: {
  name: Path<TFormValues>;
  label: string;
  type?: string;
  invalidMessage?: string;
  DynamicErrorView?: JSX.Element
}) {
  // const tr = X.useTranslator();
  const { register, formState: { errors } } = useFormContext<TFormValues>();
  const error = errors[props.name]?.message?.toString();
  // console.log({ error })
  return (
    <div>
      {/*
      <label className="block text-sm font-medium mb-1">{label}</label>
      */}

      <div className="relative">
        <input
          type={props.type}
          {...register(props.name as any)}
          autoComplete={props.type === "email" ? "email" : undefined}
          placeholder={props.label}
          className={X.clsx(
            "w-full border rounded-lg px-4",
            error ? "border-danger" : "border-black",
            "placeholder:text-[12px] placeholder:text-gray placeholder:leading-[47px]",
            "text-[14px] leading-[47px]",
            "focus:border-black focus:outline-none",
          )}
        />
        {error && (
          <X.Image
            src={WarningImage}
            alt="Warning"
            className="absolute w-[20px] h-auto top-1/2 right-4 -translate-y-1/2"
          />
        )}
      </div>

      {props.DynamicErrorView ? props.DynamicErrorView :
        <ErrorView
          error={error}
          invalidMessage={props.invalidMessage}
          requiredMessage={
            "Veuillez sélectionner une valeur"
            //   tr({
            //   en: "Please select a value",
            //   fr: "Veuillez sélectionner une valeur",
            // })
          }
        />}
      {/* <ErrorView
        error={error}
        invalidMessage={props.invalidMessage}
        requiredMessage={tr({
          en: "Required information",
          fr: "Information requise",
        })}
      /> */}
    </div>
  );
}


type SelectOption = {
  value: string;
  label: string;
};


export type SelectFieldProps = React.ComponentProps<typeof SelectField>;

export function SelectField<TFormValues extends FieldValues>(props: {
  name: Path<TFormValues>;
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  invalidMessage?: string;
  DynamicErrorView?: JSX.Element
  /**
   * used in a modal or something that is NOT pre-rendered, making it safe to
   * display `<Select />` directly, avoiding hydration issues
   */
  clientOnly?: boolean;
}) {
  // const tr = X.useTranslator();
  const instance = X.useInstance();
  const { control, formState: { errors }, trigger } = useFormContext<TFormValues>();
  const error = errors[props.name]?.message?.toString();

  if (!props.clientOnly && !instance.isMounted()) {
    return (
      <div className="text-[14px] py-[13px] rounded-lg bg-black/3 select-none">
        {X.Nbsp}
      </div>
    );
  }

  return (
    <div>
      {props.label && (

        <label className="block text-[16px] xl:text-[20px] font-medium mb-2">
          {/* {props.label} */}
          <ReactMarkdown
            components={{
              p: ({ children }) => <>{children}</>, // ⛔️ pas de <p>
            }}
          >
            {props.label}
          </ReactMarkdown>
        </label>
      )}

      <Controller
        name={props.name}
        control={control}
        render={({ field }) => (
          <Select<SelectOption>
            {...field}
            components={{ Input: NonAutoFillableSelectInput as any }}
            options={props.options}
            value={props.options.find(option => option.value === field.value)}
            onChange={(option) => {
              field.onChange(option?.value ?? "");
            }}
            placeholder={props.placeholder ?? "Please select a value"
              //   tr({
              //   en: "Please select a value",
              //   fr: "Sélectionnez parmi notre liste",
              // })
            }
            unstyled
            className="text-[14px]"
            classNames={{
              control: ({ isFocused }) => X.clsx(
                "w-full flex items-center rounded-lg px-4 py-[13px] border text-[14px]",
                error
                  ? "border-danger"
                  : isFocused
                    ? "border-black"
                    : "border-black"
              ),
              valueContainer: () => "text-[14px] text-black", // selected value
              singleValue: () => "text-[14px] text-black",
              placeholder: () => "text-[12px] text-gray", // placeholder
              indicatorsContainer: () => "flex items-center gap-2",
              dropdownIndicator: () => "text-black",
              indicatorSeparator: () => "bg-gray-300 h-5",
              menu: () => "mt-1 border border-black rounded-lg bg-white shadow-md",
              option: ({ isSelected, isFocused }) => X.clsx(
                "px-3 py-4 text-sm cursor-pointer rounded-lg",
                isSelected ? "bg-gray-100 font-medium" : "",
                isFocused ? "bg-gray-50" : ""
              ),
            }}
            onBlur={() => {
              trigger(props.name);
              field.onBlur();
            }}
          />
        )}
      />

      {props.DynamicErrorView ? props.DynamicErrorView :
        <ErrorView
          error={error}
          invalidMessage={props.invalidMessage}
          requiredMessage={
            //   tr({
            //   en: "Please select a value",
            //   fr: "Veuillez sélectionner une valeur",
            // })
            "Please select a value"
          }
        />}
    </div>
  );
}


export type CheckboxFieldProps = React.ComponentProps<typeof CheckboxField>;

export function CheckboxField<TFormValues extends FieldValues>(props: {
  name: Path<TFormValues>;
  label: string;
}) {
  const { register, watch } = useFormContext<TFormValues>();
  const checked = watch(props.name) as boolean;

  return (
    <label className="flex items-start gap-2 cursor-pointer">
      <div className="relative h-5 w-5">
        <input
          type="checkbox"
          {...register(props.name as any)}
          className="opacity-0 absolute h-full w-full cursor-pointer"
        />
        <X.Image
          src={checked ? CheckboxChecked : CheckboxUnchecked}
          alt={checked ? "Checked" : "Unchecked"}
          className="h-full w-full"
        />
      </div>
      <span className="text-[10px] xl:text-[12px] pt-[2px] leading-tight select-none">
        {props.label}
      </span>
    </label>
  );
}


//const CountryOptions: [SelectOption, ...SelectOption[]] = [
//{ value: "+1", label: "+1 (US)" },
//{ value: "+44", label: "+44 (UK)" },
//{ value: "+32", label: "+32 (BE)" },
//{ value: "+33", label: "+33 (FR)" },
//{ value: "+888", label: "+888 (UG)" },
//// Add more as needed
//];

export type PhoneFieldProps = React.ComponentProps<typeof PhoneField>;

export function PhoneField<TFormValues extends FieldValues>(props: {
  name: Path<TFormValues>;
  label: string;
  DynamicErrorView?: JSX.Element
  /**
   * used in a modal or something that is NOT pre-rendered, making it safe to
   * display `<Select />` directly, avoiding hydration issues
   */
  clientOnly?: boolean;
}) {
  // const tr = X.useTranslator();
  const instance = X.useInstance();
  const { setValue, getValues, formState: { errors }, trigger } = useFormContext<TFormValues>();
  const error = errors[props.name]?.message?.toString();
  const countryOptions = useCountryOptions();
  const initialFormValue = getValues(props.name) as string | undefined;

  const [country, setCountry] = X.useState<SelectOption | undefined>(() => {
    if (!countryOptions) {
      return undefined;
    }

    if (!initialFormValue) {
      return countryOptions[0];
    }

    const [prefix] = initialFormValue.split(".");
    const option = countryOptions.find(entry => entry.value === prefix);

    if (option) {
      return option;
    }
  });

  X.useEffect([countryOptions, country], () => {
    if (!country && countryOptions) {
      if (!initialFormValue) {
        return setCountry(countryOptions[0]);
      }

      const [prefix] = initialFormValue.split(".");
      const option = countryOptions.find(entry => entry.value === prefix);

      if (option) {
        setCountry(option);
      }
    }
  });

  const [localNumber, setLocalNumber] = X.useState(() => {
    if (initialFormValue) {
      const [, localNumber] = initialFormValue.split(".");
      return localNumber ?? "";
    }

    return "";
  });

  const ready = countryOptions && country;
  const hiddenId = X.useId();
  const localNumberId = X.useId();

  X.useEffect([ready, localNumber, props.name, setValue], () => {
    if (!ready) {
      return;
    }

    // drop leading zero, and chars that are not numeric
    const cleanNumber = localNumber.replace(/^0+/, "").replace(/\D/g, "");
    const combined = `${country.value}.${cleanNumber}`;
    setValue(props.name, combined as any);
  });

  if (!ready || (!props.clientOnly && !instance.isMounted())) {
    return (
      <div className="text-[14px] py-[13px] rounded-lg bg-black/3 select-none">
        {X.Nbsp}
      </div>
    );
  }

  const handleHiddenPhoneInput = (rawValue: string) => {
    const parsed = parsePhoneNumberFromString(rawValue);

    if (parsed && parsed.isValid()) {
      const prefix = `+${parsed.countryCallingCode}`;
      const option = countryOptions.find(entry => entry.value === prefix);

      if (option) {
        setCountry(option);
        setLocalNumber(parsed.nationalNumber);
        return;
      }
    }

    // fallback if parsing fails, just store raw number
    setLocalNumber(rawValue.replace(/\D/g, ""));
  };

  return (
    <div>
      {/* hidden input for browser autocomplete */}
      <input
        id={hiddenId}
        type="tel"
        autoComplete="tel"
        style={{ display: "none" }}
        onChange={(e) => handleHiddenPhoneInput(e.target.value)}
      />

      <div className="relative flex gap-2">
        <Select
          options={countryOptions}
          value={country}
          components={{ Input: NonAutoFillableSelectInput as any }}
          onChange={(selected) => setCountry(selected!)}
          getOptionLabel={(option) => option.label}
          formatOptionLabel={(option, { context }) =>
            // when closed, just show "+1"
            context === "value" ? option.value : option.label
          }
          unstyled
          className="text-[14px]"
          classNames={{
            control: ({ isFocused }) => X.clsx(
              "w-full min-w-[104px] flex items-center rounded-lg px-4 py-[13px] border text-[14px]",
              error
                ? "border-danger"
                : isFocused
                  ? "border-black"
                  : "border-black"
            ),
            valueContainer: () => "text-[14px] text-black",
            singleValue: () => "text-[14px] text-black",
            placeholder: () => "text-[12px] text-gray",
            indicatorsContainer: () => "flex items-center gap-2",
            dropdownIndicator: () => "text-black",
            indicatorSeparator: () => "bg-gray-300 h-5",
            menu: () => "mt-1 border border-black rounded-lg bg-white shadow-md min-w-max",
            option: ({ isSelected, isFocused }) => X.clsx(
              "px-3 py-4 text-sm cursor-pointer rounded-lg",
              isSelected ? "bg-gray-100 font-medium" : "",
              isFocused ? "bg-gray-50" : ""
            ),
          }}
        />

        <input
          id={localNumberId}
          type="tel"
          onBlur={() => {
            trigger(props.name);
          }}
          placeholder={props.label}
          value={localNumber.replace(/\D/g, "")}
          onChange={(e) => {
            const cleaned = e.target.value.replace(/\D/g, "");
            setLocalNumber(cleaned);
          }}
          className={X.clsx(
            "w-full border rounded-lg px-4",
            error ? "border-danger" : "border-black",
            "placeholder:text-[12px] placeholder:text-gray placeholder:leading-[47px]",
            "text-[14px] leading-[47px]",
            "focus:border-black focus:outline-none"
          )}
        />
        {error && (
          <X.Image
            src={WarningImage}
            alt="Warning"
            className="absolute w-[20px] h-auto top-1/2 right-4 -translate-y-1/2"
          />
        )}
      </div>

      {props.DynamicErrorView ? props.DynamicErrorView :
        <ErrorView
          error={error}
          // invalidMessage={props.invalidMessage}
          requiredMessage={
            "Please select a value"
            //   tr({
            //   en: "Please select a value",
            //   fr: "Veuillez sélectionner une valeur",
            // })
          }
        />}
      {/* <ErrorView
        error={error}
        invalidMessage={tr({ en: "Invalid phone number", fr: "Numéro invalide" })}
        requiredMessage={tr({ en: "Required phone number", fr: "Numéro requis" })}
      /> */}
    </div>
  );
}


function NonAutoFillableSelectInput(props: React.ComponentProps<typeof components.Input>) {
  const id = X.useId();

  return (
    <components.Input
      {...props}
      name={`ignore-autofill-${id}`}
      autoComplete="off"
    />
  );
}


function useCountryOptions() {
  const locale = X.useLocale();
  const instance = X.useInstance();
  const localeRef = X.useRef<X.Locale>(locale);

  const countryOptions = X.useStateRef<SelectOption[] | null>(() => {
    return (
      countryOptionsCache[locale].type === "ready"
        ? countryOptionsCache[locale].options
        : null
    );
  });

  X.useEffect([locale], () => {
    localeRef.current = locale;

    X.matchType(countryOptionsCache[locale], {
      "ready": state => {
        if (state.options !== countryOptions.current) {
          countryOptions.set(state.options);
        }
      },
      "loading": state => {
        // avoid setting state if the component was unmounted
        instance.await(state.optionsPromise).then(options => {
          if (localeRef.current === locale) {
            countryOptions.set(options);
          }
        });
      },
      "idle": () => {
        const allOptionsPromises = getAllCountryOptions();
        const localizedOptionsPromise = allOptionsPromises[locale];

        // avoid setting state if the component was unmounted
        instance.await(localizedOptionsPromise).then(options => {
          if (localeRef.current === locale) {
            countryOptions.set(options);
          }
        });
      },
    })
  });

  return countryOptions.current;
}


/**
 * get country options, ideally from cache.
 *
 * this can be called either within a PhoneInput component, getting either
 * precached or on a page that will open a modal containing a form, avoiding
 * loading times.
 */
function getAllCountryOptions() {
  return Object.fromEntries(
    Object.entries(countryOptionsCache)
      .map(([locale, state]) => [locale as X.Locale, X.matchType(state, {
        "loading": state => state.optionsPromise,
        "ready": state => Promise.resolve(state.options),
        "idle": () => loadLocalizedCountryOptions(locale as X.Locale),
      })])
  ) as {
      [k in X.Locale]: Promise<SelectOption[]>
    };
}


async function loadLocalizedCountryOptions(locale: X.Locale): Promise<SelectOption[]> {
  if (countryOptionsCache[locale].type === "ready") {
    return countryOptionsCache[locale].options;
  }

  if (countryOptionsCache[locale].type === "loading") {
    return await countryOptionsCache[locale].optionsPromise;
  }

  const optionsPromise = (
    X.matchKey(locale, {
      // need expanded map for tree shaking and general import optimization
      en: () => import("./countries-en.json"),
      fr: () => import("./countries-fr.json"),
      zh: () => import("./countries-en.json"),
      ko: () => import("./countries-en.json"),
    })
      .then(loadedModule => loadedModule.default)
      .catch(err => {
        console.error(`Failed to load localized country list for locale "${locale}"`, err);
        // returns the  default, statically-imported list that contains only country codes
        return DefaultCountryOptions;
      })
  );

  countryOptionsCache[locale] = {
    type: "loading",
    optionsPromise,
  };

  return await optionsPromise;
}


function ErrorView(props: {
  error: string | undefined;
  invalidMessage?: string;
  requiredMessage?: string;
}) {
  // const tr = X.useTranslator();

  if (!props.error) {
    return null;
  }

  const invalidValue = "Invalid value";//tr({ en: "Invalid value", fr: "Information incorrecte" });

  const message = mapError(props.error, {
    [fieldError.required]: props.requiredMessage ?? "Required field",//tr({ en: "Required field", fr: "Champ requis" }),
    [fieldError.invalidFormat]: "Invalid format",//tr({ en: "Invalid format", fr: "Format invalide" }),
    [fieldError.invalidValue]: invalidValue,
  }) ?? invalidValue;

  return (
    <p className="text-danger text-[12px] mt-1">
      {message}
    </p>
  );
}

export function DynamicErrorView(props: {
  error: string | undefined;
  invalidMessage?: string;
  requiredMessage?: string;
  invalidValue?: string;
  invalideFormat?: string;
}) {
  // const tr = X.useTranslator();

  if (!props.error) {
    return null;
  }

  const invalidValue = props.invalidValue || "Invalid value";//tr({ en: "Invalid value", fr: "Information incorrecte" });

  const message = mapError(props.error, {
    [fieldError.required]: props.requiredMessage ?? "Required field",//tr({ en: "Required field", fr: "Champ requis" }),
    [fieldError.invalidFormat]: props.invalideFormat ?? "Invalid format",//tr({ en: "Invalid format", fr: "Format invalide" }),
    [fieldError.invalidValue]: invalidValue
  }) ?? invalidValue;

  return (
    <p className="text-danger text-[12px] mt-1">
      {message}
    </p>
  );
}

function mapError(value: string, cases: { [k in FieldError]: string }): string | undefined {
  return cases[value as FieldError];
}
